import { useState, useEffect, useRef } from "react";
import { Mic, MicOff, X, ChevronUp, Hand, Volume2, Settings } from "lucide-react";
import { cn } from "@vc/lib/utils";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/clientWeb/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu";
import {
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverContent,
} from "@/clientWeb/components/ui/popover";
import {
  useDeviceEnumeration,
  useDevicesStore,
  useMediaPreview,
  useMicLevel,
} from "@/features/devices";
import { useMeetingSessionStore } from "@/features/meeting/store/meeting-session.store";
import { getDeviceLabel, useMicReminder, playTestTone } from "../hooks/utils";

interface MicControlProps {
  onOpenSettings?: (tab: "audio" | "video" | "advanced") => void;
  compact?: boolean;
  /** Host-controls gate from the feature layer — when false, the toggle is
   *  inert (mic-access disabled by the host). */
  disabled?: boolean;
  /**
   * Called instead of toggling when [disabled]. A blocked control that simply
   * does nothing reads as broken — the user needs to be told the host turned it
   * off. Supplying this keeps the button clickable so the click can be
   * answered; without it the button is inert as before.
   */
  onBlocked?: () => void;
  /**
   * The bar holding this button is off screen: auto-hidden, or collapsed while
   * something is full screen. The muted reminder is drawn over the page, not
   * inside the bar, so it no longer disappears with the bar by itself — left
   * up, it would point at nothing, or double the one full screen shows.
   */
  offscreen?: boolean;
}

export function MicControl({ onOpenSettings, compact = false, disabled = false, onBlocked, offscreen = false }: MicControlProps) {
  const t = useTranslation();
  const { mics, speakers } = useDeviceEnumeration();
  const selectedMicId = useDevicesStore((s) => s.selectedMicId);
  const selectedSpeakerId = useDevicesStore((s) => s.selectedSpeakerId);
  const setSelectedMicId = useDevicesStore((s) => s.selectMic);
  const setSelectedSpeakerId = useDevicesStore((s) => s.selectSpeaker);
  const micOn = useMeetingSessionStore((s) => s.micOn);
  const onToggleMic = useMeetingSessionStore((s) => s.toggleMic);
  const canUseMicrophone = !disabled;
  const { audioStream: micStream } = useMediaPreview({ audio: micOn, video: false });
  const micLevel = useMicLevel(micStream);

  const [audioPickerOpen, setAudioPickerOpen] = useState(false);
  const [audioDeviceTab, setAudioDeviceTab] = useState<"mic" | "speaker">("mic");
  const [micDeviceMenuOpen, setMicDeviceMenuOpen] = useState(false);
  const [speakerDeviceMenuOpen, setSpeakerDeviceMenuOpen] = useState(false);

  const {
    micReminderOpen,
    setMicReminderOpen,
    micOffCount,
    setMicOffCount,
    micReminderDismissed,
    handleDismissMicReminder,
  } = useMicReminder();

  const prevMicOnRef = useRef(micOn);

  useEffect(() => {
    if (audioPickerOpen) return;
    setMicDeviceMenuOpen(false);
    setSpeakerDeviceMenuOpen(false);
  }, [audioPickerOpen]);

  useEffect(() => {
    if (prevMicOnRef.current && !micOn && !micReminderDismissed && micOffCount < 2) {
      setMicReminderOpen(true);
      setMicOffCount((prev) => prev + 1);
    }
    prevMicOnRef.current = micOn;
  }, [micOn, micOffCount, micReminderDismissed, setMicReminderOpen, setMicOffCount]);

  return (
    <div className="relative">
      <div className="group flex items-center transition-all duration-300 ease-out hover:scale-105 hover:-translate-y-1 active:scale-110 overflow-hidden rounded-full bg-vc-surface-elevated">
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={canUseMicrophone ? onToggleMic : onBlocked}
                disabled={!canUseMicrophone && !onBlocked}
                className={cn(
                  "relative grid place-items-center transition-all duration-200 cursor-pointer",
                  compact ? "h-11 w-11" : "h-10 w-10",
                  !canUseMicrophone
                    ? "bg-vc-surface-elevated/30 cursor-not-allowed opacity-50"
                    : !micOn
                    ? "bg-[#ea4335]/20 ring-2 ring-[#ea4335]/40 hover:bg-[#ea4335]/30"
                    : "hover:bg-vc-surface-hover",
                )}
                aria-label={micOn ? t("controlBar.turnOffMicrophone") : t("controlBar.turnOnMicrophone")}
                aria-pressed={!micOn}
              >
                {micOn ? (
                  <div className="relative">
                    <Mic className={cn("text-white", compact ? "h-4 w-4" : "h-5 w-5")} />
                    <div className="pointer-events-none absolute -right-4 top-1/2 flex -translate-y-1/2 items-center gap-0.5 opacity-60 group-hover:opacity-0 transition-opacity">
                      {[0, 1, 2].map((i) => (
                        <span
                          key={i}
                          className="w-[3px] rounded-full bg-white"
                          style={{
                            height: `${6 + Math.min(14, micLevel * 22) + i * 2}px`,
                            animation: `micBars 520ms ease-in-out ${i * 120}ms infinite alternate`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <MicOff className={cn("text-white", compact ? "h-4 w-4" : "h-5 w-5")} />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={8} className="border-none capitalize">
              {micOn ? t("controlBar.turnOffMicrophone") : t("controlBar.turnOnMicrophone")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

   <DropdownMenu
  open={audioPickerOpen}
  onOpenChange={(open) => {
    setAudioPickerOpen(open);
    if (open) {
      setAudioDeviceTab("mic");
      setMicDeviceMenuOpen(false);
      setSpeakerDeviceMenuOpen(false);
    }
  }}
>
  {micOn &&
    <DropdownMenuTrigger asChild>
      <button
        style={{ display: "flex" }}
        type="button"
        className={cn(
          // Shown on hover with a mouse, and always on a touch screen, which has
          // no hover: there it was invisible, as if phones had no mic picker,
          // while the camera's arrow beside it showed.
          "grid flex cursor-pointer place-items-center opacity-0 transition group:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100",
          compact ? "h-11 w-7" : "h-10 w-7"
        )}
        aria-label={t("controlBar.audioDeviceSettings")}
      >
        <ChevronUp className="h-4 w-4" />
      </button>
    </DropdownMenuTrigger>}
  <DropdownMenuContent
    align="start"
    className="w-[380px] p-2 shadow-xl bg-card text-foreground"
  >
    <div className="flex gap-2">
      <DropdownMenu open={micDeviceMenuOpen} onOpenChange={setMicDeviceMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={() => {
              setAudioDeviceTab("mic");
              setSpeakerDeviceMenuOpen(false);
            }}
            className={cn(
              "flex min-w-0 flex-1 items-center gap-2 bg-card rounded-full border px-3 py-2 text-left transition cursor-pointer",
              audioDeviceTab === "mic"
                ? "border-slate-300 dark:border-border bg-slate-100 dark:bg-muted"
                : "border-slate-200 dark:border-border bg-card hover:bg-slate-50 dark:hover:bg-muted",
            )}
          >
            <Mic className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {getDeviceLabel(
                mics.find((m) => m.deviceId === selectedMicId),
                t("controlBar.microphone"),
              )}
            </span>
            <ChevronUp className="h-4 w-4 shrink-0 rotate-180 text-slate-500 dark:text-foreground/80" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="top"
          align="start"
          className="min-w-[320px] bg-card text-foreground shadow-xl"
        >
          <div className="px-3 pb-1 pt-2 text-xs text-muted-foreground">{t("controlBar.systemDefault")}</div>
          <DropdownMenuRadioGroup
            value={selectedMicId}
            onValueChange={(v) => setSelectedMicId(v)}
          >
            {mics.map((d) => (
              <DropdownMenuRadioItem
                key={d.deviceId}
                value={d.deviceId}
                className="cursor-pointer rounded-md px-3 py-2"
              >
                <span className="truncate">{d.label || t("controlBar.microphone")}</span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          <div className="px-3 pb-3 pt-2">
            <div className="flex items-center gap-3">
              <Mic className="h-4 w-4 text-muted-foreground" />
              <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-muted">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-primary"
                  style={{ width: `${Math.round((micLevel ?? 0) * 100)}%` }}
                />
              </div>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu open={speakerDeviceMenuOpen} onOpenChange={setSpeakerDeviceMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={() => {
              setAudioDeviceTab("speaker");
              setMicDeviceMenuOpen(false);
            }}
            className={cn(
              "flex min-w-0 flex-1 items-center gap-2 rounded-full border px-3 py-2 text-left transition cursor-pointer",
              audioDeviceTab === "speaker"
                ? "border-slate-300 dark:border-border bg-slate-100 dark:bg-muted"
                : "border-slate-200 dark:border-border bg-card hover:bg-slate-50 dark:hover:bg-muted",
            )}
          >
            <Volume2 className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {getDeviceLabel(
                speakers.find((s) => s.deviceId === selectedSpeakerId),
                t("controlBar.speaker"),
              )}
            </span>
            <ChevronUp className="h-4 w-4 shrink-0 rotate-180 text-slate-500 dark:text-foreground/80" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="top"
          align="start"
          className="min-w-[320px] bg-card text-foreground shadow-xl"
        >
          <div className="px-3 pb-1 pt-2 text-xs text-muted-foreground">{t("controlBar.systemDefault")}</div>
          <DropdownMenuRadioGroup
            value={selectedSpeakerId}
            onValueChange={(v) => setSelectedSpeakerId(v)}
          >
            {speakers.map((d) => (
              <DropdownMenuRadioItem
                key={d.deviceId}
                value={d.deviceId}
                className="cursor-pointer rounded-md px-3 py-2"
              >
                <span className="truncate">{d.label || t("controlBar.speaker")}</span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator className="bg-slate-200" />
          <DropdownMenuItem
            className="cursor-pointer rounded-md px-3 py-2"
            onSelect={() => {
              playTestTone();
            }}
          >
            <Volume2 className="mr-2 h-4 w-4" />
            {t("controlBar.testSpeakers")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Settings Button */}
      {onOpenSettings && (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onOpenSettings("audio")}
                className="flex items-center justify-center p-2 text-slate-600 hover:text-slate-900 dark:text-foreground/80 hover:bg-slate-100 dark:hover:bg-muted rounded-lg transition"
                aria-label={t("controlBar.audioSettings")}
              >
                <Settings className={cn("transition-transform duration-200 hover:rotate-90", compact ? "h-3.5 w-3.5" : "h-4 w-4")} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={8} className="border-none capitalize">
              {t("controlBar.audioSettings")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  </DropdownMenuContent>
</DropdownMenu>


      </div>

      {/* Mic Reminder Popover.
          Portalled, like the bar's tooltips and menus. Drawn inside the bar it
          could not rise above a presentation: the bar's translate makes it a
          stacking context, which capped its z-index at the bar's own level, and
          a shared document's or screen's z-10 to z-30 layers painted over it.
          On phones the scrolling button row clipped it altogether. */}
      <Popover open={micReminderOpen && !offscreen} onOpenChange={setMicReminderOpen}>
        <PopoverAnchor className="pointer-events-none absolute inset-0" />
        <PopoverContent
          side="top"
          sideOffset={8}
          hideWhenDetached
          // A reminder, not a dialog: focus stays on the mic button, and a
          // click elsewhere leaves it up until X or its timeout, as before.
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
          className="w-64 bg-card rounded-lg border-0 shadow-xl p-4 duration-300"
        >
          <button
            onClick={handleDismissMicReminder}
            className="absolute cursor-pointer top-2 right-2 text-gray-400 hover:text-gray-600 dark:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 animate-[hand-wave_1.5s_ease-in-out_infinite]">
              <Hand className="h-4 w-4 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-foreground">{t("controlBar.turnOnYourMicrophone")}</p>
              <p className="text-xs text-gray-900 dark:text-foreground/80 mt-1">{t("controlBar.canTurnOnWhileSpeaking")}</p>
            </div>
          </div>
          <PopoverArrow width={8} height={4} className="fill-card" />
        </PopoverContent>
      </Popover>
    </div>
  );
}
